import React, { ReactNode, VFC } from 'react';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import {
  HOME_MEDIA_CARD_BORDER_RADIUS,
  HOME_MEDIA_DEFAULT_MEDIA_PADDING_TOP,
} from './constants';

export type HomeMediaCardProps = {
  href: string;
  className?: string;
  /** When true, uses MUI elevation 1 (theme.shadows[1]). */
  elevated?: boolean;
  /** When true with elevated false, draws a light border like the legacy region card. */
  bordered?: boolean;
  /**
   * Aspect ratio for the media area via padding-top percentage trick.
   * @default HOME_MEDIA_DEFAULT_MEDIA_PADDING_TOP
   */
  mediaAspectPaddingTop?: string;
  imageUrl: string | null;
  imageAlt: string;
  topLeft?: ReactNode;
  topRight?: ReactNode;
  children?: ReactNode;
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: HOME_MEDIA_CARD_BORDER_RADIUS,
    backgroundColor: theme.palette.background.paper,
  },
  rootBordered: {
    border: '1px solid #E5E5E5',
  },
  rootFlatShadow: {
    boxShadow: 'none',
  },
  action: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    transition: 'opacity 0.2s',
    '&:hover': {
      textDecoration: 'none',
      opacity: 0.97,
    },
  },
  mediaOuter: {
    position: 'relative',
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: HOME_MEDIA_CARD_BORDER_RADIUS,
  },
  mediaInner: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: HOME_MEDIA_CARD_BORDER_RADIUS,
  },
  mediaFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  image: {
    objectFit: 'cover',
  },
  noImage: {
    backgroundColor: '#eee',
  },
  overlay: {
    position: 'absolute',
    zIndex: 1,
    display: 'flex',
    pointerEvents: 'auto',
  },
  topLeft: {
    top: theme.spacing(1),
    left: theme.spacing(1),
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  topRight: {
    top: theme.spacing(1),
    right: theme.spacing(1),
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
  },
}));

const HomeMediaCard: VFC<HomeMediaCardProps> = ({
  href,
  className,
  elevated = true,
  bordered = false,
  mediaAspectPaddingTop = HOME_MEDIA_DEFAULT_MEDIA_PADDING_TOP,
  imageUrl,
  imageAlt,
  topLeft,
  topRight,
  children,
}) => {
  const classes = useStyles();
  return (
    <Card
      className={clsx(
        classes.root,
        bordered && classes.rootBordered,
        !elevated && classes.rootFlatShadow,
        className,
      )}
      elevation={elevated ? 1 : 0}
      square={false}
    >
      <CardActionArea
        component="a"
        href={href}
        className={classes.action}
        disableRipple
      >
        <div className={classes.mediaOuter}>
          <div
            className={classes.mediaInner}
            style={{ paddingTop: mediaAspectPaddingTop }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={imageAlt}
                className={clsx(classes.mediaFill, classes.image)}
                loading="lazy"
              />
            ) : (
              <div className={clsx(classes.mediaFill, classes.noImage)} />
            )}
            {topLeft != null && (
              <div className={clsx(classes.overlay, classes.topLeft)}>
                {topLeft}
              </div>
            )}
            {topRight != null && (
              <div className={clsx(classes.overlay, classes.topRight)}>
                {topRight}
              </div>
            )}
          </div>
        </div>
        {children != null && <div className={classes.body}>{children}</div>}
      </CardActionArea>
    </Card>
  );
};

export default HomeMediaCard;
